import { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRightLeft, Plus, TrendingDown, TrendingUp, Minus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getVisuals, TREND_ICON } from "./treemap-utils";

function CompareSlot({ node, index, onRemove }) {
  if (!node) {
    return (
      <div
        className="flex-1 min-w-[220px] max-w-[320px] h-full rounded-xl border-2 border-dashed border-white/[0.06] flex flex-col items-center justify-center gap-2 text-white/15"
        data-testid={`compare-slot-empty-${index}`}
      >
        <Plus className="w-5 h-5" />
        <span className="text-[10px] uppercase tracking-wider">Drag tile here</span>
      </div>
    );
  }

  const v = getVisuals(node.state, node.urgency);
  const TIcon = TREND_ICON[node.trend] || Minus;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="flex-1 min-w-[220px] max-w-[320px] h-full rounded-xl border overflow-hidden relative"
      style={{ background: v.bg, borderColor: v.border }}
      data-testid={`compare-slot-${node.id}`}
    >
      <button
        onClick={() => onRemove(index)}
        className="absolute top-2 right-2 w-5 h-5 rounded-full bg-black/40 flex items-center justify-center text-white/40 hover:text-white/80 transition-colors z-10"
        data-testid={`compare-remove-${node.id}`}
      >
        <X className="w-3 h-3" />
      </button>

      <div className="p-4 h-full flex flex-col">
        <span className="text-[10px] uppercase tracking-wider text-white/35 font-medium">{node.title}</span>
        <span className="text-2xl font-bold mt-1" style={{ color: v.accent }}>{node.metric}</span>
        <div className="flex items-center gap-1 mt-1 text-white/30">
          <TIcon className="w-3 h-3" />
          <span className="text-[10px]">{node.metric_context}</span>
        </div>

        <div className="flex gap-1 mt-2.5">
          <Badge className={`${v.badge} border text-[9px] px-1.5 py-0`}>{v.label}</Badge>
        </div>

        {node.impact_potential && (
          <div className="mt-2">
            <p className="text-[9px] uppercase tracking-wider text-white/20 mb-0.5">Impact Potential</p>
            <p className="text-[11px] text-white/55">{node.impact_potential}</p>
          </div>
        )}

        {node.suggested_action && (
          <div className="mt-auto pt-2 border-t border-white/[0.04]">
            <p className="text-[9px] uppercase tracking-wider text-primary/40 mb-0.5">Action</p>
            <p className="text-[11px] text-white/65 leading-snug">{node.suggested_action}</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function TradeOff({ left, right, allNodes }) {
  if (!left || !right) return null;

  const crossFromLeft = (left.cross_impacts || []).find(ci => ci.node_id === right.id);
  const crossFromRight = (right.cross_impacts || []).find(ci => ci.node_id === left.id);
  const insight = crossFromLeft?.description || crossFromRight?.description;

  if (!insight) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scaleY: 0 }}
      animate={{ opacity: 1, scaleY: 1 }}
      className="flex flex-col items-center justify-center px-2 gap-1 shrink-0"
      data-testid="trade-off-indicator"
    >
      <ArrowRightLeft className="w-4 h-4 text-amber-400/40" />
      <p className="text-[9px] text-amber-400/50 text-center leading-tight max-w-[140px]">{insight}</p>
    </motion.div>
  );
}

export function ComparePanel({ slots, onRemoveSlot, onClose, allNodes }) {
  const filledSlots = slots.filter(Boolean);

  const systemInsight = useMemo(() => {
    if (filledSlots.length < 2) return null;
    const states = filledSlots.map(n => n.state);
    const allRed = states.every(s => s === "red");
    const hasGreen = states.some(s => s === "green");

    if (allRed) return "All selected areas need attention — consider prioritizing the highest-urgency item first.";
    if (hasGreen) return "Improving areas can offset risk — consider reallocating resources from green zones.";

    const crossLinks = filledSlots.flatMap(n =>
      (n.cross_impacts || []).filter(ci => filledSlots.some(s => s.id === ci.node_id))
    );
    if (crossLinks.length > 0) return crossLinks[0].description;
    return "Compare metrics and actions to find the best investment of effort.";
  }, [filledSlots]);

  return (
    <motion.div
      initial={{ y: 300, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 300, opacity: 0 }}
      transition={{ type: "spring", stiffness: 280, damping: 28 }}
      className="fixed bottom-0 left-0 right-0 z-30 border-t border-white/[0.06]"
      style={{ background: "rgba(2,4,2,0.94)", backdropFilter: "blur(24px)" }}
      data-testid="compare-panel"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
      }}
    >
      {/* System insight */}
      {systemInsight && (
        <div className="px-6 pt-3 pb-1">
          <p className="text-[11px] text-white/35 italic text-center" data-testid="compare-insight">
            {systemInsight}
          </p>
        </div>
      )}

      <div className="px-6 pt-2 pb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4 text-white/30" />
            <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">Compare Mode</span>
            <span className="text-[10px] text-white/20">{filledSlots.length}/3 slots</span>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}
            className="text-white/30 hover:text-white text-xs h-7 px-2" data-testid="close-compare">
            <X className="w-3.5 h-3.5 mr-1" /> Close
          </Button>
        </div>

        <div className="flex items-stretch gap-0 h-[200px]">
          {[0, 1, 2].map((i) => (
            <div key={`slot-${i}`} className="contents">
              {i > 0 && slots[i - 1] && slots[i] && (
                <TradeOff left={slots[i - 1]} right={slots[i]} allNodes={allNodes} />
              )}
              <AnimatePresence mode="sync">
                <CompareSlot key={slots[i]?.id || `empty-${i}`} node={slots[i]} index={i} onRemove={onRemoveSlot} />
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
